import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Mail, Lock, User, Phone, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff, Check, MailCheck, Gift, X,
} from 'lucide-react';
import { InvestLanguageFlags } from '../components/InvestLanguageFlags';
import { facebookEnabled, facebookLoginPopup } from '../lib/facebookAuth';
import { GoogleAuthButton } from '../components/GoogleAuthButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BG = 'https://images.unsplash.com/photo-1694880912546-5d9619757fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYXR2aWElMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzg0MTcwOTc1fDA&ixlib=rb-4.1.0&q=85';

export default function RegisterPage() {
  const { register, loginWithFacebook } = useAuth();
  const { t, language } = useLanguage();
  const L = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', name: '', phone: '', is_seller: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [inviterName, setInviterName] = useState('');
  const [inviteCode, setInviteCode] = useState(() => {
    try { return localStorage.getItem('kolv_ref') || ''; } catch (e) { return ''; }
  });
  const [codeStatus, setCodeStatus] = useState('idle'); // idle | checking | valid | invalid

  // Resolve the inviter's name as the code is entered (typed or captured from a link).
  useEffect(() => {
    const code = inviteCode.trim();
    if (!code) { setInviterName(''); setCodeStatus('idle'); return; }
    setCodeStatus('checking');
    const id = setTimeout(() => {
      axios.get(`${API}/referrals/resolve/${encodeURIComponent(code)}`)
        .then(({ data }) => {
          setInviterName(data?.name || '');
          setCodeStatus('valid');
          // Persist a valid code so Google/Facebook signup also attribute.
          try { localStorage.setItem('kolv_ref', code); } catch (e) { /* ignore */ }
        })
        .catch(() => { setInviterName(''); setCodeStatus('invalid'); });
    }, 350);
    return () => clearTimeout(id);
  }, [inviteCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const startFacebook = useCallback(async () => {
    setError('');
    try {
      const token = await facebookLoginPopup();
      const result = await loginWithFacebook(token);
      if (result.success) {
        navigate(result.user?.onboarded ? '/' : '/welcome');
      } else {
        setError(result.error);
      }
    } catch (e) {
      if (e?.message !== 'cancelled') {
        setError(L('Neizdevās pieslēgties ar Facebook', 'Facebook sign-in failed', 'Не удалось войти через Facebook'));
      }
    }
  }, [loginWithFacebook, navigate, L]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError(L('Paroles nesakrīt', 'Passwords do not match', 'Пароли не совпадают'));
      return;
    }
    if (formData.password.length < 6) {
      setError(L('Parolei jābūt vismaz 6 rakstzīmēm', 'Password must be at least 6 characters', 'Пароль должен содержать не менее 6 символов'));
      return;
    }
    setLoading(true);
    const result = await register({
      email: formData.email,
      password: formData.password,
      name: formData.name,
      phone: formData.phone || null,
      is_seller: formData.is_seller,
      ref: inviteCode.trim() || undefined,
    });
    if (result.success) {
      // Region is chosen during onboarding at /welcome, so send the user there.
      // Sellers are routed to shop registration after onboarding.
      if (result.requiresVerification) setRegisteredEmail(formData.email);
      else navigate('/welcome');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const field = (key, placeholder, Icon, type = 'text', extra = {}) => (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
      <input
        name={key}
        type={type}
        value={formData[key]}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full h-12 pl-11 pr-11 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors"
        data-testid={`${key.replace(/([A-Z])/g, '-$1').toLowerCase()}-input`}
        {...extra}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ background: '#0A0D0B' }} data-testid="register-page">
      {/* Cinematic backdrop */}
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30 scale-105" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 8%, rgba(10,13,11,0.15), rgba(10,13,11,0.9))' }} />
      </div>

      <div className="relative z-10 min-h-full flex items-center justify-center py-10 px-5">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-[28px] border border-white/10 bg-[#0A0D0B]/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] p-8 sm:p-10"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {registeredEmail ? (
          <div className="text-center" data-testid="verification-pending">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-amber-500/15 flex items-center justify-center">
              <MailCheck className="w-8 h-8 text-amber-400" />
            </div>
            <h1 className="text-white text-3xl font-medium tracking-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {L('Pārbaudi savu e-pastu!', 'Check your email!', 'Проверьте свою почту!')}
            </h1>
            <p className="text-white/60 text-sm mb-1">
              {L('Mēs nosūtījām apstiprinājuma saiti uz:', 'We sent a confirmation link to:', 'Мы отправили ссылку для подтверждения на:')}
            </p>
            <p className="font-semibold text-amber-400 mb-6 break-all">{registeredEmail}</p>
            <p className="text-white/50 text-sm mb-7">
              {L(
                'Atver e-pastu un noklikšķini uz saites, lai aktivizētu kontu. Ja neredzi vēstuli, pārbaudi spam mapi.',
                "Open the email and click the link to activate your account. If you don't see it, check your spam folder.",
                'Откройте письмо и нажмите на ссылку, чтобы активировать аккаунт. Если письма нет, проверьте папку «Спам».')}
            </p>
            <Link to="/login">
              <button className="w-full h-12 rounded-full bg-white text-[#0A0D0B] font-semibold text-sm flex items-center justify-center gap-2 hover:bg-white/90 transition-colors" data-testid="go-to-login-btn">
                {L('Uz pieteikšanos', 'Go to login', 'К входу')} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        ) : (
          <>
            <div className="flex justify-center mb-6">
              <InvestLanguageFlags />
            </div>
            <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-semibold mb-5 transition-colors" data-testid="register-home-link">
              <ArrowLeft className="w-3.5 h-3.5" /> {L('Uz sākumu', 'Home', 'На главную')}
            </Link>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-500/80 mb-3">KoLV · Baltijas kopiena</p>
            <h1 className="text-white text-4xl sm:text-5xl font-light tracking-tighter mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {L('Izveido kontu', 'Create account', 'Создать аккаунт')}<span className="text-amber-500">.</span>
            </h1>
            <p className="text-white/60 text-sm mb-8">
              {L('Pievienojies vietējai kopienai dažu sekunžu laikā.', 'Join the local community in a few seconds.', 'Присоединяйтесь к местному сообществу за несколько секунд.')}
            </p>

            {inviterName && (
              <div className="flex items-center gap-2.5 mb-6 p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30" data-testid="register-inviter-banner">
                <Gift className="w-4 h-4 text-amber-400 shrink-0" />
                <p className="text-amber-200/90 text-sm">
                  {L(`Tevi uzaicināja ${inviterName}`, `${inviterName} invited you`, `Вас пригласил(а) ${inviterName}`)}
                </p>
              </div>
            )}

            <GoogleAuthButton
              className="w-full h-12 rounded-full bg-white text-[#0A0D0B] font-semibold text-sm flex items-center justify-center gap-3 hover:bg-white/90 transition-colors"
              testid="register-google-btn"
              onDone={(u) => navigate(u?.onboarded ? '/' : '/welcome')}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              {L('Reģistrēties ar Google', 'Sign up with Google', 'Зарегистрироваться через Google')}
            </GoogleAuthButton>

            {facebookEnabled() && (
              <button
                onClick={startFacebook}
                className="w-full h-12 mt-3 rounded-full bg-[#1877F2] text-white font-semibold text-sm flex items-center justify-center gap-3 hover:bg-[#166fe0] transition-colors"
                data-testid="register-facebook-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.89v2.25h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
                {L('Reģistrēties ar Facebook', 'Sign up with Facebook', 'Зарегистрироваться через Facebook')}
              </button>
            )}

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-white/40 text-xs uppercase tracking-widest">{L('vai', 'or', 'или')}</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {error && (
                <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              {field('name', `${t('name')} *`, User, 'text', { required: true })}
              {field('email', `${t('email')} *`, Mail, 'email', { required: true })}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder={`${t('password')} *`}
                    className="w-full h-12 pl-11 pr-10 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors"
                    data-testid="password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder={`${t('confirmPassword')} *`}
                    className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {field('phone', t('phone'), Phone, 'tel')}

              <div>
                <div className="relative">
                  <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    name="inviteCode"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase().slice(0, 12))}
                    placeholder={L('Uzaicinājuma kods (nav obligāts)', 'Invitation code (optional)', 'Код приглашения (необязательно)')}
                    className={`w-full h-12 pl-11 pr-11 rounded-2xl bg-white/5 border text-white placeholder-white/40 text-sm tracking-wider focus:bg-white/[0.07] focus:outline-none transition-colors ${codeStatus === 'invalid' ? 'border-red-500/60 focus:border-red-500/80' : codeStatus === 'valid' ? 'border-emerald-500/50 focus:border-emerald-500/70' : 'border-white/10 focus:border-amber-500/60'}`}
                    data-testid="invite-code-input"
                  />
                  {inviteCode.trim() && (
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2" data-testid="invite-code-status">
                      {codeStatus === 'valid'
                        ? <Check className="w-4 h-4 text-emerald-400" />
                        : codeStatus === 'invalid'
                        ? <X className="w-4 h-4 text-red-400" />
                        : <span className="text-[10px] text-white/40">{L('pārbauda…', 'checking…', 'проверка…')}</span>}
                    </span>
                  )}
                </div>
                {codeStatus === 'invalid' && (
                  <p className="mt-1.5 pl-1 text-xs text-red-400" data-testid="invite-code-error">
                    {L('Šāds kods neeksistē — pārbaudi, vai nav drukas kļūdas.', "This code doesn't exist — check for typos.", 'Такого кода нет — проверьте опечатки.')}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, is_seller: !prev.is_seller }))}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border transition-colors text-left ${formData.is_seller ? 'bg-amber-500/10 border-amber-500/50' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                data-testid="seller-checkbox"
              >
                <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-colors ${formData.is_seller ? 'bg-amber-500' : 'bg-white/10 border border-white/20'}`}>
                  {formData.is_seller && <Check className="w-3.5 h-3.5 text-[#0A0D0B]" />}
                </span>
                <span className="text-white/80 text-sm">{t('iAmSeller')}</span>
              </button>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-60 !mt-5"
                style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
                data-testid="register-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t('register')} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

            <p className="text-center text-white/50 text-sm mt-6">
              {t('haveAccount')}{' '}
              <Link to="/login" className="text-amber-500 font-semibold hover:underline" data-testid="login-link">
                {t('login')}
              </Link>
            </p>
          </>
        )}
      </motion.div>
      </div>
    </div>
  );
}
